import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const TimesheetReport = () => {
  const [loading, setLoading] = useState(false);
  // Set default month to current month
  const getCurrentMonth = () => {
    const currentDate = new Date();
    return `${String(currentDate.getMonth() + 1).padStart(2, '0')}-${currentDate.getFullYear()}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [timesheetData, setTimesheetData] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const toastShownRef = useRef(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarMonthLabel, setCalendarMonthLabel] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthValue = `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value: monthValue, label: monthLabel });
    }
    
    return months;
  };

  const monthOptions = getMonthOptions();

  // Auto-fetch data when month or page changes
  useEffect(() => {
    if (selectedMonth) {
      toastShownRef.current = false; // Reset toast flag when month or page changes
      fetchTimesheetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, pagination.currentPage]);

  const fetchTimesheetData = async () => {
    if (!selectedMonth) {
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      // Parse month value (format: "MM-YYYY")
      const [month, year] = selectedMonth.split('-');
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);

      const payload = {
        action: 'get-timesheet-summary',
        currentUserId: currentUserId,
        month: monthNum,
        year: yearNum,
        page: pagination.currentPage,
        limit: pagination.limit
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setTimesheetData(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
        if (result.data && result.data.length === 0 && !toastShownRef.current) {
          toast.info('Timesheet not found for the selected month.');
          toastShownRef.current = true;
        } else if (result.data && result.data.length > 0) {
          toastShownRef.current = false; // Reset if data is found
        }
      } else {
        toast.error(result?.message || 'Failed to fetch timesheet data');
        setTimesheetData([]);
        setPagination(prev => ({
          ...prev,
          totalPages: 0,
          totalCount: 0,
          hasNextPage: false,
          hasPreviousPage: false
        }));
      }
    } catch (error) {
      console.error('Error fetching timesheet data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
      setTimesheetData([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 0,
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false
      }));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  // Build calendar view for selected month showing present/absent/holiday
  const buildCalendarData = (monthValue, summaryData) => {
    if (!monthValue) return;
    const [month, year] = monthValue.split('-').map(v => parseInt(v, 10));
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const todayComparable = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const dayStatusMap = new Map();
    (summaryData || []).forEach(item => {
      if (item?.date) {
        const parts = item.date.replace(/\//g, '-').split('-');
        if (parts.length === 3) {
          const [d, m, y] = parts.map(v => parseInt(v, 10));
          if (m === month && y === year) {
            // Check the status field from API response
            const status = item.status?.toLowerCase() || 'present';
            if (status === 'holiday' || status === 'weekoff' || status === 'week off') {
              dayStatusMap.set(d, 'holiday');
            } else if (status === 'leave') {
              dayStatusMap.set(d, 'leave');
            } else if (status === 'present') {
              dayStatusMap.set(d, 'present');
            } else {
              dayStatusMap.set(d, 'absent');
            }
          }
        }
      }
    });

    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month - 1, day);
      const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
      const isFuture = dateObj.getTime() > todayComparable;
      const isSunday = dateObj.getDay() === 0; // Sunday
      let status = 'absent';
      if (dayStatusMap.has(day)) {
        status = dayStatusMap.get(day);
      } else if (isFuture) {
        status = 'future';
      } else if (isSunday && !dayStatusMap.has(day)) {
        status = 'holiday';
      }
      days.push({
        day,
        dayLabel,
        status
      });
    }

    setCalendarDays(days);
    const date = new Date(year, month - 1, 1);
    setCalendarMonthLabel(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
  };

  const fetchCalendar = async (record) => {
    if (!API_URL || !record?.employeeId) return;

    // Show modal immediately with loading state
    setCalendarModalOpen(true);
    setCalendarLoading(true);
    const [selMonth, selYear] = (selectedMonth || '').split('-');
    const monthNum = parseInt(selMonth, 10);
    const yearNum = parseInt(selYear, 10);
    try {
      const payload = {
        action: 'get-checkin-checkout-summary',
        currentUserId: record.employeeId,
        page: 1,
        limit: 150,
        ...(monthNum && { month: monthNum }),
        ...(yearNum && { year: yearNum })
      };

      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const result = response.data;
      if (result?.success) {
        buildCalendarData(selectedMonth, result.data || []);
        setCalendarModalOpen(true);
      } else {
        toast.error(result?.message || 'Failed to load calendar');
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCalendarLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                Timesheet Summary
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View daily timesheet records including check-ins.
              </p>
            </div>
          </div>

          {/* Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  <div className="d-flex flex-wrap align-items-end" style={{ gap: '15px' }}>
                    <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
                      <label htmlFor="month" className="form-label" style={{ marginBottom: '5px' }}>
                        Select Month
                      </label>
                      <select
                        id="month"
                        className="form-control"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        style={{ padding: '8px 12px', fontSize: '14px', height: '40px' }}
                      >
                        {monthOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Tab Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  {/* Tab Navigation */}
                  <div className="mb-3" style={{ borderBottom: '2px solid #0094a9' }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setActiveTab('summary')}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: activeTab === 'summary' ? '#0094a9' : 'transparent',
                        color: activeTab === 'summary' ? '#fff' : '#6b7280',
                        border: 'none',
                        borderBottom: activeTab === 'summary' ? '2px solid #0094a9' : '2px solid transparent',
                        borderRadius: '0',
                        marginBottom: '-2px'
                      }}
                    >
                      Summary
                    </button>
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'summary' && (
                    <div className="table-container">
                      {loading ? (
                        <div className="text-center" style={{ padding: '40px' }}>
                          <Loading size={60} />
                        </div>
                      ) : timesheetData.length === 0 ? (
                        <div className="text-center" style={{ padding: '40px', color: '#6b7280' }}>
                          <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
                          <p style={{ fontSize: '16px', margin: 0 }}>No records found</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="employee-table">
                            <thead>
                              <tr>
                                <th>S.No</th>
                                <th>Employee Name</th>
                                <th>Total Working Days</th>
                                <th>Expected Working Days</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {timesheetData.map((record, index) => {
                                const workingDays = record.totalWorkingDays || 0;
                                const expectedDays = record.expectedWorkingDays || 0;
                                const isComplete = workingDays >= expectedDays;
                                const difference = workingDays - expectedDays;
                                
                                return (
                                  <tr
                                    key={record.employeeId || index}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => fetchCalendar(record)}
                                    title="Click to view calendar"
                                  >
                                    <td>{getSerialNumber(index)}</td>
                                    <td>{record.employeeName || '-'}</td>
                                    <td>
                                      <strong style={{ fontSize: '16px', color: '#111827' }}>
                                        {workingDays}
                                      </strong>
                                      {record.totalDaysInMonth && (
                                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '5px' }}>
                                          / {record.totalDaysInMonth} days
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      <strong style={{ fontSize: '16px', color: '#0094a9' }}>
                                        {expectedDays}
                                      </strong>
                                    </td>
                                    <td>
                                      {isComplete ? (
                                        <span className="badge badge-success" style={{
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          borderRadius: '6px',
                                          backgroundColor: '#10b981',
                                          color: '#fff'
                                        }}>
                                          <i className="fas fa-check-circle mr-1"></i>
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="badge badge-warning" style={{
                                          padding: '6px 12px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          borderRadius: '6px',
                                          backgroundColor: '#f59e0b',
                                          color: '#fff'
                                        }}>
                                          <i className="fas fa-exclamation-circle mr-1"></i>
                                          {difference < 0 ? `${Math.abs(difference)} days short` : 'Pending'}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {timesheetData.length > 0 && pagination.totalPages > 0 && (
                        <div className="employee-pagination">
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Modal */}
      {calendarModalOpen && (
        <div className="modal-overlay" onClick={() => setCalendarModalOpen(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>{calendarMonthLabel || 'Attendance Calendar'}</h3>
              <button className="modal-close" onClick={() => setCalendarModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {calendarLoading ? (
                <div className="text-center" style={{ padding: '20px' }}>
                  <Loading size={60} />
                </div>
              ) : (
                <>
                  <div className="d-flex mb-3" style={{ gap: '12px', fontSize: '12px' }}>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#10b981', borderRadius: '2px', marginRight: '6px' }}></span>Present</span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#c80000', borderRadius: '2px', marginRight: '6px' }}></span>Absent</span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#4338ca', borderRadius: '2px', marginRight: '6px' }}></span>Holiday</span>
                    <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#f59e0b', borderRadius: '2px', marginRight: '6px' }}></span>Leave</span>
                  </div>
                  <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
                    {calendarDays.map((day) => (
                      <div
                        key={day.day}
                        style={{
                          padding: '7px',
                          borderRadius: '6px',
                          textAlign: 'center',
                          backgroundColor: day.status === 'present'
                            ? '#ecfdf3'
                            : day.status === 'future'
                              ? '#f3f4f6'
                              : day.status === 'holiday'
                                ? '#eef2ff'
                                : day.status === 'leave'
                                  ? '#fef3c7'
                                  : '#fee2e2',
                          color: day.status === 'present'
                            ? '#065f46'
                            : day.status === 'future'
                              ? '#6b7280'
                              : day.status === 'holiday'
                                ? '#4338ca'
                                : day.status === 'leave'
                                  ? '#92400e'
                                  : '#991b1b',
                          border: '1px solid #e5e7eb',
                          fontWeight: 600,
                          opacity: day.status === 'future' ? 0.7 : 1
                        }}
                      >
                        <div style={{ fontSize: '16px' }}>{day.day}</div>
                        <div style={{ fontSize: '11px', marginTop: '2px' }}>{day.dayLabel}</div>
                        <div style={{ fontSize: '12px', fontWeight: 500, marginTop: '4px' }}>
                          {day.status === 'present'
                            ? 'Present'
                            : day.status === 'future'
                              ? '—'
                              : day.status === 'holiday'
                                ? 'Holiday'
                                : day.status === 'leave'
                                  ? 'Leave'
                                  : 'Absent'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetReport;
